import { useContext, useEffect, useMemo } from 'react'

import { Switch } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faChevronDown, faClinicMedical, faSchool, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'

import { EUROPEAN_DATE, OrderDirections, Contexts } from '../../utils/constants'
import { selectUser } from '../../reducers/UserReducer'
import useLocalStorageOld from '../../hooks/UseLocalStorage'
import IconButton from '../antd/Buttons/IconButton'
import { getTimezoneLessMoment } from '../../utils/momentjs'
import Loading from '../../HOC/Loading'
import InfiniteScroll from '../../HOC/InfiniteScroll'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { MaccsInternshipMessageContext } from '../../Providers/MaccsinternshipMessageProvider'
import { GlobalContext } from '../../Providers/GlobalProvider'

import '../../assets/message-box.scss'

const LOCAL_STORAGE_SETTINGS_KEY = 'internshipMessagesBox'

const MaccsMessageBox = () => {
  const { unreadMessages } = useContext(GlobalContext)
  const { display, internshipsMessages, setDisplay, setSelectedInternship } = useContext(MaccsInternshipMessageContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [settings, setSettings] = useLocalStorageOld(LOCAL_STORAGE_SETTINGS_KEY, { unread: false, orderDirection: OrderDirections.DESC })
  const isSchoolUser = useMemo(() => user.context === Contexts.SCHOOL, [user])

  useEffect(() => {
    internshipsMessages.setParameters(settings)
  }, [settings, internshipsMessages.setParameters])

  const messages = useMemo(() => {
    let data = []

    Object.keys(internshipsMessages.all).forEach(key => {
      data = data.concat(internshipsMessages.all[key])
    })

    return data
  }, [internshipsMessages.all])

  const toggleDisplayMessages = () => {
    setDisplay(!display)
  }

  const handleBottomReach = internshipsMessages.next

  const messagesListJSX = useMemo(() => messages.map((item, index) => (
    <li key={`${item.id}-${index}`} onClick={() => setSelectedInternship(item)}>
      <div className={`message-item ${item.unreadMessages ? 'new-messages' : ''}`}>
        <div className='message-text'>
          <div className='inner-message-row'>
            <span className='message-icon'>
              <FontAwesomeIcon icon={isSchoolUser ? faClinicMedical : faSchool} />
            </span>
            <span className='inner-message-text'>{isSchoolUser ? item.institution?.name : item.school?.name}</span>
          </div>
          <div className='inner-message-row'>
            <span className='message-icon'>
              <FontAwesomeIcon icon={faUserGraduate} />
            </span>
            <span className='inner-message-text'>{item.assistant === null ? t('Without assistant') : `${item.assistant.firstname} ${item.assistant.lastname}`}</span>
          </div>
          <div className='inner-message-row'>
            <span className='message-icon'>
              <FontAwesomeIcon icon={faCalendar} />
            </span>
            <span className='inner-message-text'>{getTimezoneLessMoment(item.startDate).format(EUROPEAN_DATE)} - {getTimezoneLessMoment(item.endDate).format(EUROPEAN_DATE)}</span>
          </div>
        </div>
        {item.unreadMessages > 0 && (
          <div className='messages-count'>
            <span className='message-count-circle'>{item.unreadMessages}</span>
          </div>
        )}
      </div>
    </li>
  )), [messages, isSchoolUser, t, setSelectedInternship])

  return (
    <div className={`message-box ${display ? 'displayed' : ''}`}>
      <div className='message-box-header' onClick={toggleDisplayMessages}>
        <div className='title'>
          {t('Messaging')}
          {unreadMessages > 0 && <span className='message-count-circle'>{unreadMessages > 99 ? '99+' : unreadMessages}</span>}
        </div>
        <span>
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </div>
      <div className='filter-box-wrapper'>
        <div className='option-group'>
          <label>{t('Show only unread messages')}</label>
          <Switch checked={settings.unread} onChange={() => setSettings({ ...settings, unread: !settings.unread })} />
        </div>
        <div className='option-group'>
          <label>{t('Sorting by date')}</label>
          <IconButton
            className={`sort-button ${settings.orderDirection === OrderDirections.ASC ? 'up' : ''}`}
            icon={faArrowDown}
            onClick={() => setSettings({ ...settings, orderDirection: settings.orderDirection === OrderDirections.ASC ? OrderDirections.DESC : OrderDirections.ASC })}
          />
        </div>
      </div>
      <div className='separation-line' />
      <div className='messages-container'>
        {messages.length > 0 && (
          <InfiniteScroll
            onBottomReach={handleBottomReach}
            hasMore={!internshipsMessages.isLastPage}
            loading={internshipsMessages.loading}
            loadingChild={<Loading loading />}
          >
            <ul className='messages-list'>
              {messagesListJSX}
              {internshipsMessages.loading && <Loading loading />}
            </ul>
          </InfiniteScroll>
        )}
        {messages.length === 0 && (
          <Loading loading={internshipsMessages.loading}>
            {internshipsMessages.data.length === 0 && (
              <div className='no-message-notification'>
                {t(settings.showOnlyUnreadMessages ? 'You don\'t have unread messages' : 'You don\'t have messages')}
              </div>
            )}
          </Loading>
        )}
      </div>
    </div>
  )
}

export default MaccsMessageBox
